import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, Typography, Alert, Button } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useLocation } from "react-router-dom";
import { useAircraft } from "../../../hooks/useAircraft";
import { Aircraft } from "../../../types";
import { AircraftTable } from "./components/AircraftTable";
import { AircraftPreview } from "./components/AircraftPreview";

export const AircraftAdmin: React.FC = () => {
  const { t } = useTranslation();
  const { loading, aircraft, completionStatus, error } = useAircraft();
  const location = useLocation();
  const [selectedAircraft, setSelectedAircraft] = useState<Aircraft | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [imageError, setImageError] = useState(false);
  const tableRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Create a map of aircraft ID to make/model for linked aircraft display
  const aircraftMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    aircraft.forEach(a => {
      map[a.id] = `${a.make} ${a.model}`;
    });
    return map;
  }, [aircraft]);

  // Helper function to scroll to aircraft row
  const scrollToAircraft = (index: number) => {
    setTimeout(() => {
      if (tableRef.current) {
        const row = tableRef.current.querySelector(`tbody tr:nth-child(${index + 1})`);
        row?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  };

  // Helper function to find and select aircraft by ID
  const selectAircraftById = (aircraftId: string): boolean => {
    const index = aircraft.findIndex(a => a.id === aircraftId);
    if (index !== -1) {
      setSelectedAircraft(aircraft[index]);
      setSelectedIndex(index);
      scrollToAircraft(index);
      return true;
    }
    return false;
  };

  // Helper function to select first aircraft
  const selectFirstAircraft = () => {
    if (aircraft.length > 0 && !selectedAircraft) {
      setSelectedAircraft(aircraft[0]);
      setSelectedIndex(0);
    }
  };

  // Focus container on mount
  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  // Handle aircraft selection and restoration from navigation state
  useEffect(() => {
    if (!aircraft || aircraft.length === 0) return;

    // Try to restore from navigation state
    const state = location.state as { selectedAircraftId?: string } | undefined;
    const selectedId = state?.selectedAircraftId;
    
    if (selectedId && selectAircraftById(selectedId)) {
      return;
    }
    
    // Default to first aircraft
    selectFirstAircraft();
  }, [aircraft, location.state]);

  // Helper function for keyboard navigation
  const handleArrowKeyNavigation = (direction: 'up' | 'down') => {
    if (!aircraft || aircraft.length === 0) return;

    let newIndex = selectedIndex ?? 0;
    
    if (direction === 'down') {
      newIndex = Math.min(newIndex + 1, aircraft.length - 1);
    } else {
      newIndex = Math.max(newIndex - 1, 0);
    }

    setSelectedIndex(newIndex);
    setSelectedAircraft(aircraft[newIndex]);
    setImageError(false);

    // Scroll into view
    if (tableRef.current) {
      const row = tableRef.current.querySelector(`tbody tr:nth-child(${newIndex + 1})`);
      row?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if typing in input
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        handleArrowKeyNavigation('down');
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        handleArrowKeyNavigation('up');
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [aircraft, selectedIndex]);

  const handleSelectAircraft = (aircraft: Aircraft, index: number) => {
    setSelectedAircraft(aircraft);
    setSelectedIndex(index);
    setImageError(false);
  };

  // Helper to get aircraft status
  const getAircraftStatus = (aircraft: Aircraft): string => {
    if (aircraft.linked_to) return t('screens.superAdmin.aircraftAdmin.status.locked');
    if (completionStatus[aircraft.id]) return t('screens.superAdmin.aircraftAdmin.status.deployed');
    return t('screens.superAdmin.aircraftAdmin.status.labeling');
  };

  // Helper to prepare aircraft data for export
  const prepareAircraftExportData = (): { headers: string[], rows: string[][] } => {
    const headers = [
      t('screens.superAdmin.aircraftAdmin.exportHeaders.make'),
      t('screens.superAdmin.aircraftAdmin.exportHeaders.model'),
      t('screens.superAdmin.aircraftAdmin.exportHeaders.length'),
      t('screens.superAdmin.aircraftAdmin.exportHeaders.wingspan'),
      t('screens.superAdmin.aircraftAdmin.exportHeaders.tailHeight'),
      t('screens.superAdmin.aircraftAdmin.exportHeaders.status'),
      t('screens.superAdmin.aircraftAdmin.exportHeaders.id')
    ];
    
    const rows = aircraft.map(a => [
      a.make || "",
      a.model || "",
      a.length?.toFixed(1) || "",
      a.wingspan?.toFixed(1) || "",
      a.tail_height?.toFixed(1) || "",
      getAircraftStatus(a),
      a.id
    ]);
    
    return { headers, rows };
  };

  // Helper to download file
  const downloadFile = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const { headers, rows } = prepareAircraftExportData();
    
    // Convert to TSV format
    const tsvContent = [
      headers.join("\t"),
      ...rows.map(row => row.join("\t"))
    ].join("\n");
    
    const filename = `aircraft_list_${new Date().toISOString().split('T')[0]}.tsv`;
    downloadFile(tsvContent, filename, "text/tab-separated-values;charset=utf-8;");
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>{t('screens.superAdmin.aircraftAdmin.loading')}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      tabIndex={0}
      sx={{
        p: 2,
        height: 'calc(100vh - 250px)',
        display: 'flex',
        flexDirection: 'column',
        outline: 'none'
      }}
    >
      <Stack spacing={3} sx={{ height: "100%" }}>
        <Typography variant="h5">{t('screens.superAdmin.aircraftAdmin.title')}</Typography>
        
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{ flex: 1, minHeight: 0, position: 'relative' }}
        >
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <AircraftTable
              aircraft={aircraft}
              completionStatus={completionStatus}
              selectedAircraft={selectedAircraft}
              onSelectAircraft={handleSelectAircraft}
              tableRef={tableRef}
              aircraftMap={aircraftMap}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <Box sx={{
                px: 2,
                py: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'background.paper'
              }}>
                <Typography
                  variant="body2"
                  color="black"
                  dangerouslySetInnerHTML={{
                    __html: t('screens.superAdmin.aircraftAdmin.aircraftDeployed', {
                      count: Object.values(completionStatus).filter(Boolean).length
                    })
                  }}
                />
              </Box>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                disabled={aircraft.length === 0}
              >
                {t('screens.superAdmin.aircraftAdmin.exportButton')}
              </Button>
            </Box>
          </Box>
          
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <AircraftPreview
              selectedAircraft={selectedAircraft}
              imageError={imageError}
              onImageError={() => setImageError(true)}
              aircraftMap={aircraftMap}
            />
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
};